import React, {
    FC,
    createContext,
    ReactNode,
    useRef,
    useMemo,
    useContext,
    useState,
    useEffect,
} from 'react';

import Axios, { CancelTokenSource } from 'axios';
import { useLocation } from 'react-router-dom';
import {
    IIndexDbConsultaResult,
    indexDbConsulta,
} from '../../../shared/helpers/indexDb';

import { useSearchParams } from '../../../shared/hooks';
import {
    BookType,
    getFiltersData,
    loadSearchData,
    paginate,
} from '../services/AccessibilityService';

type PaginationType = {
    itemsPerPage: number;
};

type FilterItem = {
    code: string;
    description: string;
};

type FilterType = {
    typeOfWork: FilterItem[];
    libraries: FilterItem[];
};

interface AccessibilityContextProps {
    faqElementRef: React.MutableRefObject<HTMLElement | null>;
    searchInputRef: React.MutableRefObject<HTMLElement | null>;
    hasFaqs: boolean;
    setHasFaqs: React.Dispatch<React.SetStateAction<boolean>>;
    hasSearch: boolean;
    setHasSearch: React.Dispatch<React.SetStateAction<boolean>>;
    reqCancelTokenSrc: CancelTokenSource | null;
    setReqCancelTokenSrc: React.Dispatch<
        React.SetStateAction<CancelTokenSource | null>
    >;
    cancelRequest: () => void;
    loadingScreen: boolean;
    setLoadingScreen: React.Dispatch<React.SetStateAction<boolean>>;
    indexDbConsulta: IIndexDbConsultaResult;
    searchData: BookType[] | null;
    setSearchData: React.Dispatch<React.SetStateAction<BookType[] | null>>;
    paginationConfig: PaginationType;
    filters: FilterType;
    setFilters: React.Dispatch<React.SetStateAction<FilterType>>;
    selectedFilterTypeOfWork: string | null;
    setSelectedFilterTypeOfWork: React.Dispatch<
        React.SetStateAction<string | null>
    >;
    selectedFilterLibraries: string | null;
    setSelectedFilterLibraries: React.Dispatch<
        React.SetStateAction<string | null>
    >;
    searchText: string | null;
    setSearchText: React.Dispatch<React.SetStateAction<string | null>>;
    processSearchData: (
        search: string,
        page?: number,
        setLoadings?: boolean,
        _accessibleTitleSearch?: 'S' | 'N',
    ) => Promise<boolean>;
    totalResults: number;
    setTotalResults: React.Dispatch<React.SetStateAction<number>>;
    clearFlowStates: () => Promise<void>;
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    selectedFilterTitleType: 'TA' | 'TT';
    setSelectedFilterTitleType: React.Dispatch<
        React.SetStateAction<'TA' | 'TT'>
    >;
    accessibleTitleSearch: 'S' | 'N';
    setAccessibleTitleSearch: React.Dispatch<React.SetStateAction<'S' | 'N'>>;
}

const AccessibilityContext = createContext<
    AccessibilityContextProps | undefined
>(undefined);

const LAST_ACCESSIBILITY_SEARCH_KEY = 'lastAccessibilitySearchUrl';

interface AccessibilityContextProviderProps {
    children: ReactNode;
}

const AccessibilityContextProvider: FC<AccessibilityContextProviderProps> = ({
    children,
}) => {
    const searchInputRef = useRef<HTMLElement | null>(null);
    const faqElementRef = useRef<HTMLElement | null>(null);

    const [hasFaqs, setHasFaqs] = useState<boolean>(false);
    const [hasSearch, setHasSearch] = useState<boolean>(false);
    const [reqCancelTokenSrc, setReqCancelTokenSrc] =
        useState<CancelTokenSource | null>(null);
    const [loadingScreen, setLoadingScreen] = useState<boolean>(false);
    const [searchData, setSearchData] = useState<BookType[] | null>(null);
    const [filters, setFilters] = useState<FilterType>({
        typeOfWork: [],
        libraries: [],
    });
    const [totalResults, setTotalResults] = useState<number>(0);
    const [searchText, setSearchText] = useState<string | null>(null);

    const [selectedFilterTypeOfWork, setSelectedFilterTypeOfWork] = useState<
        string | null
    >(null);
    const [selectedFilterLibraries, setSelectedFilterLibraries] = useState<
        string | null
    >(null);
    const [page, setPage] = useState<number>(1);
    const [selectedFilterTitleType, setSelectedFilterTitleType] = useState<
        'TT' | 'TA'
    >('TT');
    const [accessibleTitleSearch, setAccessibleTitleSearch] = useState<
        'S' | 'N'
    >('N');

    const paginationConfig = {
        itemsPerPage: 10,
    };

    const { getSearchParam } = useSearchParams();
    const { pathname } = useLocation();

    const saveLastSearchUrl = (
        search: string,
        currentPage: number,
        currentAccessibleTitleSearch: 'S' | 'N',
    ) => {
        const params = new URLSearchParams();

        params.set('search', search);

        if (currentPage > 1) {
            params.set('page', String(currentPage));
        }

        if (selectedFilterLibraries) {
            params.set('library', selectedFilterLibraries);
        }

        if (selectedFilterTypeOfWork) {
            params.set('typeOfWork', selectedFilterTypeOfWork);
        }

        if (selectedFilterTitleType) {
            params.set('titleType', selectedFilterTitleType);
        }

        if (currentAccessibleTitleSearch) {
            params.set(
                'accessibleTitleSearch',
                currentAccessibleTitleSearch,
            );
        }

        const url = `/pesquisa_acessibilidade?${params.toString()}`;
        localStorage.setItem(LAST_ACCESSIBILITY_SEARCH_KEY, url);
    };

    const processSearchData = async (
        search: string,
        pageArg?: number,
        setLoadings?: boolean,
        _accessibleTitleSearch: 'S' | 'N' = 'N',
    ): Promise<boolean> => {
        if (_accessibleTitleSearch === 'N' && search === null) {
            return false;
        }

        if (setLoadings === undefined) setLoadings = true;

        if (setLoadings) setLoadingScreen(true);

        const cancelTokenSrc = Axios.CancelToken.source();
        setReqCancelTokenSrc(cancelTokenSrc);
        const { token } = cancelTokenSrc;

        let data = await loadSearchData(search, _accessibleTitleSearch, token);

        if (data !== false) {
            const filterData = await getFiltersData(data);
            setFilters(filterData);

            if (selectedFilterLibraries) {
                data = data.filter(
                    item =>
                        item.dados_adicionais.B.length > 0 &&
                        item.dados_adicionais.B.some(
                            x => x.code === selectedFilterLibraries,
                        ),
                );
            }

            if (selectedFilterTypeOfWork) {
                data = data.filter(
                    item => item.cod_tipo_obra === selectedFilterTypeOfWork,
                );
            }

            if (selectedFilterTitleType === 'TA') {
                data = data.filter(
                    item => item?.possui_acessibilidade === 'S',
                );
            }

            setTotalResults(data.length);

            let currentPage = pageArg === undefined ? 1 : pageArg;

            const maxPage = Math.ceil(
                data.length / paginationConfig.itemsPerPage,
            );

            if (currentPage > maxPage) {
                currentPage = maxPage;
            }

            if (currentPage < 1 || Number.isNaN(currentPage)) {
                currentPage = 1;
            }

            data.sort((a, b) => b.descricao.localeCompare(a.descricao));

            const paginatedData = paginate(
                data,
                paginationConfig.itemsPerPage,
                currentPage,
            );

            setPage(currentPage);
            setSearchText(search);
            setSearchData(paginatedData);

            saveLastSearchUrl(search, currentPage, _accessibleTitleSearch);

            if (setLoadings) setLoadingScreen(false);

            setTimeout(() => {
                const consultaContent =
                    document.querySelector('.consultaContent');
                if (consultaContent) {
                    consultaContent.scrollIntoView({
                        block: 'start',
                        inline: 'nearest',
                        behavior: 'smooth',
                    });
                }
            }, 300);

            return true;
        }

        if (setLoadings) setLoadingScreen(false);

        return false;
    };

    useEffect(() => {
        if (
            searchText === null &&
            !/^\/pesquisa_acessibilidade\/detalhes/.test(pathname)
        ) {
            (async () => {
                const search = getSearchParam('search');

                if (search) {
                    setSearchText(search);
                    await processSearchData(search);
                }
            })();
        }
    }, [pathname, searchText, accessibleTitleSearch]);

    const cancelRequest = (): void => {
        if (reqCancelTokenSrc !== null) {
            reqCancelTokenSrc.cancel('Request canceled by the user');
        }
    };

    const clearFlowStates = async (): Promise<void> => {
        return new Promise(resolve => {
            setSearchText(null);
            setFilters({ libraries: [], typeOfWork: [] });
            setSearchData(null);
            setTotalResults(0);
            setSelectedFilterLibraries(null);
            setSelectedFilterTypeOfWork(null);
            setSelectedFilterTitleType('TT');
            setPage(1);
            localStorage.removeItem(LAST_ACCESSIBILITY_SEARCH_KEY);
            setTimeout(() => resolve(), 500);
        });
    };

    const memoizedData = useMemo(
        (): AccessibilityContextProps => ({
            faqElementRef,
            searchInputRef,
            hasFaqs,
            setHasFaqs,
            hasSearch,
            setHasSearch,
            reqCancelTokenSrc,
            setReqCancelTokenSrc,
            cancelRequest,
            loadingScreen,
            setLoadingScreen,
            indexDbConsulta,
            searchData,
            setSearchData,
            filters,
            setFilters,
            selectedFilterTypeOfWork,
            setSelectedFilterTypeOfWork,
            selectedFilterLibraries,
            setSelectedFilterLibraries,
            searchText,
            setSearchText,
            paginationConfig,
            processSearchData,
            totalResults,
            setTotalResults,
            clearFlowStates,
            page,
            setPage,
            selectedFilterTitleType,
            setSelectedFilterTitleType,
            accessibleTitleSearch,
            setAccessibleTitleSearch,
        }),
        [
            hasFaqs,
            hasSearch,
            reqCancelTokenSrc,
            loadingScreen,
            searchData,
            filters,
            selectedFilterTypeOfWork,
            selectedFilterLibraries,
            searchText,
            totalResults,
            page,
            selectedFilterTitleType,
            accessibleTitleSearch,
        ],
    );

    return (
        <AccessibilityContext.Provider value={memoizedData}>
            {children}
        </AccessibilityContext.Provider>
    );
};

export const useAccessibilityContext = (): AccessibilityContextProps => {
    const context = useContext(
        AccessibilityContext,
    ) as AccessibilityContextProps;

    if (context === undefined) {
        throw new Error(
            'useAccessibilityContext must be used within a AccessibilityContextProvider',
        );
    }

    return context;
};

export default AccessibilityContextProvider;